import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "./ValuePage.css";

const IMAGES = {
  hero: "https://static.prod-images.emergentagent.com/jobs/86c15cca-92b5-4a67-ade7-963a28a16f58/images/98803976c37727a1b8353c9764f12c2955a0acbab6e1f58a61d51858bc723691.png",
  beforeAfter: "https://static.prod-images.emergentagent.com/jobs/6fff8dd0-1d4f-48ea-adac-4db1831de70f/images/25a3ee897452a5bdf626b6cfbbebcc4ee5a0cd3699ecfdea4d6a592ce3ec1859.png",
  roiChart: "https://static.prod-images.emergentagent.com/jobs/6fff8dd0-1d4f-48ea-adac-4db1831de70f/images/613666c25aaa20991d5cc67615fd4fbadf5e46c8d781f524269d38308fbae1cb.png",
};

// Animated counter hook
function useCounter(target, duration = 2000, startOnView = true) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (!startOnView) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          let start = 0;
          const increment = target / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration, startOnView]);

  return [count, ref];
}

function ROICalculator() {
  const [vehicleType, setVehicleType] = useState('motorhome');
  const [salespeople, setSalespeople] = useState(10);
  const [avgProfit, setAvgProfit] = useState(15000);
  const [extraDealsPerPerson, setExtraDealsPerPerson] = useState(1);

  const handleTypeChange = (type) => {
    setVehicleType(type);
    if (type === 'motorhome') {
      setAvgProfit(15000);
    } else {
      setAvgProfit(2800);
    }
  };

  // Simple, realistic math
  const extraDealsPerMonth = salespeople * extraDealsPerPerson;
  const monthlyAdditionalProfit = extraDealsPerMonth * avgProfit;
  const annualAdditionalProfit = monthlyAdditionalProfit * 12;

  return (
    <div className="roi-calc" data-testid="roi-calculator">
      <div className="roi-calc-inputs">
        <h3>Your Dealership</h3>
        <div className="roi-vehicle-toggle" data-testid="roi-vehicle-toggle">
          <button
            className={`roi-toggle-btn ${vehicleType === 'motorhome' ? 'active' : ''}`}
            onClick={() => handleTypeChange('motorhome')}
            data-testid="roi-toggle-motorhome"
          >
            Motorhome / RV
          </button>
          <button
            className={`roi-toggle-btn ${vehicleType === 'car' ? 'active' : ''}`}
            onClick={() => handleTypeChange('car')}
            data-testid="roi-toggle-car"
          >
            Cars / Trucks
          </button>
        </div>
        <div className="roi-input-group">
          <label>Salespeople on Your Team</label>
          <input
            type="range"
            min="1"
            max="30"
            value={salespeople}
            onChange={(e) => setSalespeople(+e.target.value)}
            data-testid="roi-salespeople-slider"
          />
          <span className="roi-input-value">{salespeople}</span>
        </div>
        <div className="roi-input-group">
          <label>Average Profit Per Deal</label>
          <input
            type="range"
            min={vehicleType === 'motorhome' ? 5000 : 1000}
            max={vehicleType === 'motorhome' ? 50000 : 10000}
            step={vehicleType === 'motorhome' ? 1000 : 500}
            value={avgProfit}
            onChange={(e) => setAvgProfit(+e.target.value)}
            data-testid="roi-profit-slider"
          />
          <span className="roi-input-value">${avgProfit.toLocaleString()}</span>
        </div>
        <div className="roi-input-group">
          <label>Extra Deals Per Salesperson / Month</label>
          <input
            type="range"
            min="1"
            max="5"
            value={extraDealsPerPerson}
            onChange={(e) => setExtraDealsPerPerson(+e.target.value)}
            data-testid="roi-extra-deals-slider"
          />
          <span className="roi-input-value">{extraDealsPerPerson}</span>
        </div>
      </div>
      <div className="roi-calc-results">
        <h3>Your Additional Profit from LeadeyePro</h3>
        <div className="roi-result-card roi-result-highlight">
          <span className="roi-result-label">Annual Additional Profit</span>
          <span className="roi-result-number roi-result-total">${annualAdditionalProfit.toLocaleString()}</span>
        </div>
        <div className="roi-results-grid">
          <div className="roi-result-card">
            <span className="roi-result-label">Extra Deals / Month</span>
            <span className="roi-result-number">{extraDealsPerMonth}</span>
            <span className="roi-result-detail">
              {salespeople} salespeople x {extraDealsPerPerson} extra deal{extraDealsPerPerson > 1 ? 's' : ''} each
            </span>
          </div>
          <div className="roi-result-card">
            <span className="roi-result-label">Monthly Additional Profit</span>
            <span className="roi-result-number">${monthlyAdditionalProfit.toLocaleString()}</span>
            <span className="roi-result-detail">
              {extraDealsPerMonth} deals x ${avgProfit.toLocaleString()} profit per deal
            </span>
          </div>
          <div className="roi-result-card roi-result-wide">
            <span className="roi-result-label">How LeadeyePro Gets You There</span>
            <span className="roi-result-detail roi-result-how">
              Instant AI responses capture leads your competitors miss. 24/7 coverage means no inquiry goes unanswered — 
              nights, weekends, holidays. Accurate pricing and live inventory builds buyer trust. 
              Each salesperson closes just {extraDealsPerPerson} more deal{extraDealsPerPerson > 1 ? 's' : ''} per month they wouldn't have gotten otherwise.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Price Sync & Sold Detection Mockup
function PriceSyncMockup() {
  return (
    <div className="sync-mockup" data-testid="price-sync-mockup">
      <div className="sync-header">
        <span className="sync-header-title">Price & Inventory Monitor</span>
        <span className="sync-header-badge sync-badge-danger">3 Issues Found</span>
      </div>
      <div className="sync-body">
        <div className="sync-item sync-item-danger">
          <div className="sync-item-icon sync-icon-red">SOLD</div>
          <div className="sync-item-info">
            <span className="sync-item-title">2024 Winnebago Revel 44E</span>
            <span className="sync-item-detail">Sold 3 days ago on website</span>
          </div>
          <div className="sync-item-status">
            <span className="sync-status-bad">Still Live on Marketplace</span>
            <span className="sync-status-sub">Buyers messaging about a unit you can't sell</span>
          </div>
        </div>
        <div className="sync-item sync-item-warning">
          <div className="sync-item-icon sync-icon-orange">$</div>
          <div className="sync-item-info">
            <span className="sync-item-title">2023 Tiffin Allegro Bus 45MP</span>
            <span className="sync-item-detail">Website: $279,998</span>
          </div>
          <div className="sync-item-status">
            <span className="sync-status-warn">Marketplace: $299,998</span>
            <span className="sync-status-sub">$20,000 overpriced — losing buyers to competitors</span>
          </div>
        </div>
        <div className="sync-item sync-item-danger">
          <div className="sync-item-icon sync-icon-red">SOLD</div>
          <div className="sync-item-info">
            <span className="sync-item-title">2022 Thor Delano 24TT</span>
            <span className="sync-item-detail">Sold last week on website</span>
          </div>
          <div className="sync-item-status">
            <span className="sync-status-bad">Still Live on Marketplace</span>
            <span className="sync-status-sub">Wasting staff time on dead leads</span>
          </div>
        </div>
        <div className="sync-item sync-item-warning">
          <div className="sync-item-icon sync-icon-orange">$</div>
          <div className="sync-item-info">
            <span className="sync-item-title">2025 Entegra Launch 19Y</span>
            <span className="sync-item-detail">Website: $104,998</span>
          </div>
          <div className="sync-item-status">
            <span className="sync-status-warn">Marketplace: $114,998</span>
            <span className="sync-status-sub">$10,000 overpriced — buyer trust eroding</span>
          </div>
        </div>
      </div>
      <div className="sync-footer">
        <span className="sync-footer-stat"><strong>Without sync:</strong> 2 sold units still live, 2 prices wrong</span>
        <span className="sync-footer-cost">Est. wasted staff time: 6+ hrs/week on dead leads & complaints</span>
      </div>
    </div>
  );
}

// AI Chat Mockup Component
function ChatMockup() {
  return (
    <div className="chat-mockup" data-testid="ai-chat-mockup">
      <div className="chat-header">
        <div className="chat-header-left">
          <div className="chat-avatar">AI</div>
          <div className="chat-header-info">
            <span className="chat-name">LeadeyePro Agent</span>
            <span className="chat-status">Active now</span>
          </div>
        </div>
        <div className="chat-header-dots">
          <span></span><span></span><span></span>
        </div>
      </div>
      <div className="chat-body">
        <div className="chat-msg chat-msg-buyer">
          <p>Hi, is the 2024 Winnebago Revel 44E still available?</p>
          <span className="chat-time">8:47 PM</span>
        </div>
        <div className="chat-msg chat-msg-ai">
          <p>Hi! Yes, the 2024 Winnebago Revel 44E is available at our Orlando location. It's a great choice - 4x4 on the Mercedes-Benz Sprinter chassis. Currently listed at $134,998.</p>
          <span className="chat-time">8:47 PM</span>
        </div>
        <div className="chat-msg chat-msg-ai">
          <p>Would you like to schedule a time to come see it? We have availability this week - what day works best for you?</p>
          <span className="chat-time">8:47 PM</span>
        </div>
        <div className="chat-msg chat-msg-buyer">
          <p>That sounds great! Could I come Saturday morning?</p>
          <span className="chat-time">8:49 PM</span>
        </div>
        <div className="chat-msg chat-msg-ai">
          <p>Saturday morning works perfectly! I'll have the Revel pulled up front and ready for you. Does 10:00 AM work? I'll also send you the full spec sheet and some photos. What's the best email to reach you?</p>
          <span className="chat-time">8:49 PM</span>
        </div>
      </div>
      <div className="chat-footer">
        <span className="chat-badge">Responded in 12 seconds - after hours</span>
      </div>
    </div>
  );
}

export default function ValuePage() {
  const [hoursSaved, hoursRef] = useCounter(80, 2000);
  const [dealsClosed, dealsRef] = useCounter(15, 2000);
  const [responseSpeed, speedRef] = useCounter(21, 1500);
  const [coverage, coverageRef] = useCounter(40, 1500);

  return (
    <div className="value-page">
      {/* Navigation */}
      <nav className="nav">
        <div className="nav-container">
          <Link to="/" className="nav-logo">
            <span className="logo-text">LeadeyePro</span>
          </Link>
          <div className="nav-links">
            <Link to="/">Home</Link>
            <a href="#roi-calculator">ROI Calculator</a>
            <a href="#methodology">Our Math</a>
            <a
              href="https://chromewebstore.google.com/detail/leadeyepro/lalbegpebklchglhpobgjnbjpieifcfe"
              target="_blank"
              rel="noopener noreferrer"
              className="nav-cta"
            >
              Add to Chrome
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="vp-hero">
        <div className="hero-bg-gradient"></div>
        <div className="vp-hero-content">
          <div className="hero-badge">Business Value Analysis</div>
          <h1 className="vp-hero-title">
            Every Hour Your Team Spends on Manual Listings
            <span className="gradient-text"> Is Revenue Left on the Table</span>
          </h1>
          <p className="vp-hero-subtitle">
            Dealerships using Facebook Marketplace spend 80+ hours per month on manual posting, 
            inquiry responses, and price updates. LeadeyePro automates all of it — and the numbers speak for themselves.
          </p>
          <a href="#roi-calculator" className="btn btn-primary" data-testid="calculate-roi-cta">
            Calculate Your ROI
          </a>
        </div>
        <div className="vp-hero-image">
          <img src={IMAGES.hero} alt="LeadeyePro Dashboard" loading="eager" />
        </div>
      </section>

      {/* The Problem */}
      <section className="vp-problem">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">The Hidden Cost of "Good Enough"</h2>
            <p className="section-subtitle">
              Most dealerships don't realize how much revenue they're leaving on the table 
              with manual Marketplace management
            </p>
          </div>
          <div className="vp-comparison">
            <div className="vp-comparison-side vp-comparison-before">
              <div className="vp-comparison-label">Without LeadeyePro</div>
              <ul className="vp-comparison-list">
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                  <div>
                    <strong>7-10 min per listing</strong>
                    <span>Manually copying title, description, price, photos from inventory to FB</span>
                  </div>
                </li>
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                  <div>
                    <strong>45+ min average response time</strong>
                    <span>Buyers message 3-5 dealers. First reply wins. You're losing deals at lunch.</span>
                  </div>
                </li>
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                  <div>
                    <strong>Prices drift out of sync</strong>
                    <span>Inventory prices change daily. Manual FB updates fall behind, creating confusion and lost trust.</span>
                  </div>
                </li>
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                  <div>
                    <strong>After-hours inquiries go cold</strong>
                    <span>40% of Marketplace messages come outside business hours. Zero response = zero sale.</span>
                  </div>
                </li>
              </ul>
            </div>
            <div className="vp-comparison-divider">
              <span>VS</span>
            </div>
            <div className="vp-comparison-side vp-comparison-after">
              <div className="vp-comparison-label">With LeadeyePro</div>
              <ul className="vp-comparison-list">
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  <div>
                    <strong>One-click posting</strong>
                    <span>30 seconds per listing. Auto-fills everything from your inventory site.</span>
                  </div>
                </li>
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  <div>
                    <strong>Instant AI responses 24/7</strong>
                    <span>Answers buyer questions in under 60 seconds with real inventory data. Always on.</span>
                  </div>
                </li>
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  <div>
                    <strong>Auto price sync</strong>
                    <span>Detects price changes and updates FB listings automatically. Always accurate.</span>
                  </div>
                </li>
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  <div>
                    <strong>Never miss another lead</strong>
                    <span>AI handles after-hours, weekends, and holidays. Your pipeline never sleeps.</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <div className="vp-comparison-image">
            <img src={IMAGES.beforeAfter} alt="Before and After comparison" loading="lazy" />
          </div>
        </div>
      </section>

      {/* Key Impact Numbers */}
      <section className="vp-impact">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">The Impact in Numbers</h2>
            <p className="section-subtitle">Based on data from dealerships with 100-300 active Marketplace listings</p>
          </div>
          <div className="vp-impact-grid">
            <div className="vp-impact-card" ref={hoursRef}>
              <span className="vp-impact-number">{hoursSaved}+</span>
              <span className="vp-impact-label">Hours Saved Monthly</span>
              <p className="vp-impact-detail">
                Posting automation saves ~24h, AI responses save ~44h, price sync saves ~30h per month. 
                That's a full-time employee's worth of work — automated.
              </p>
            </div>
            <div className="vp-impact-card" ref={dealsRef}>
              <span className="vp-impact-number">+{dealsClosed}</span>
              <span className="vp-impact-label">Additional Deals/Month</span>
              <p className="vp-impact-detail">
                Instant responses convert at 2-4x the rate of 30+ minute delays. 
                Harvard Business Review found leads contacted within 5 min are 21x more likely to qualify.
              </p>
            </div>
            <div className="vp-impact-card" ref={speedRef}>
              <span className="vp-impact-number">{responseSpeed}x</span>
              <span className="vp-impact-label">More Likely to Qualify</span>
              <p className="vp-impact-detail">
                Industry research shows response speed is the #1 factor in online lead conversion. 
                LeadeyePro responds in under 60 seconds — every time, day or night.
              </p>
            </div>
            <div className="vp-impact-card" ref={coverageRef}>
              <span className="vp-impact-number">{coverage}%</span>
              <span className="vp-impact-label">After-Hours Leads Captured</span>
              <p className="vp-impact-detail">
                40% of Marketplace inquiries come outside business hours. Without AI coverage, 
                these leads go to your fastest competitor. LeadeyePro captures every single one.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Value Breakdown */}
      <section className="vp-features">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Where the Value Comes From</h2>
            <p className="section-subtitle">Every feature is designed to directly impact your bottom line</p>
          </div>
          <div className="vp-feature-rows">
            <div className="vp-feature-row">
              <div className="vp-feature-text">
                <div className="vp-feature-value-tag">Saves ~24 hours/month</div>
                <h3>One-Click Posting & Inventory Sync</h3>
                <p>
                  Posting 150 listings manually takes 12+ hours. With LeadeyePro, your team clicks once 
                  per listing — auto-fills title, description, price, and photos directly from your inventory website. 
                  Re-sync takes minutes, not days.
                </p>
                <div className="vp-feature-math">
                  <strong>The math:</strong> 150 listings x 7 min manual = 17.5 hrs. 
                  With LeadeyePro: 150 x 0.5 min = 1.25 hrs. <em>Savings: 16+ hours per cycle.</em>
                </div>
              </div>
              <div className="vp-feature-visual">
                <img src={IMAGES.hero} alt="Dashboard showing inventory sync" loading="lazy" />
              </div>
            </div>
            <div className="vp-feature-row vp-feature-row-reverse">
              <div className="vp-feature-text">
                <div className="vp-feature-value-tag">Captures 40% more leads</div>
                <h3>24/7 AI Messenger Agent</h3>
                <p>
                  Your AI agent responds to every Marketplace inquiry instantly with real data from your inventory — 
                  vehicle specs, pricing, availability, financing options. It qualifies leads and schedules 
                  appointments while your team is off the clock.
                </p>
                <div className="vp-feature-math">
                  <strong>The math:</strong> 25 daily inquiries x 4 min each = 1.6 hrs/day manual work. 
                  AI handles it in real-time, 24/7. <em>Plus: captures the 40% of leads that come after hours.</em>
                </div>
              </div>
              <div className="vp-feature-visual">
                <ChatMockup />
              </div>
            </div>
            <div className="vp-feature-row">
              <div className="vp-feature-text">
                <div className="vp-feature-value-tag">Eliminates pricing errors</div>
                <h3>Automatic Price Sync & Sold Detection</h3>
                <p>
                  Inventory prices change daily from incentives, market shifts, and manager decisions. 
                  LeadeyePro continuously compares your website prices to Marketplace listings and flags 
                  discrepancies. One click to update all, or auto-mark sold units.
                </p>
                <div className="vp-feature-math">
                  <strong>The math:</strong> Manually checking 150+ listings for price changes: ~1.5 hrs/day. 
                  That's 30+ hrs/month of mind-numbing comparison work — <em>fully automated.</em>
                </div>
              </div>
              <div className="vp-feature-visual">
                <PriceSyncMockup />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Calculator */}
      <section id="roi-calculator" className="vp-calculator">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Calculate Your ROI</h2>
            <p className="section-subtitle">
              Adjust the sliders to match your dealership's profile and see your projected lead and profit impact
            </p>
          </div>
          <ROICalculator />
        </div>
      </section>

      {/* Methodology */}
      <section id="methodology" className="vp-methodology">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">How We Calculated This</h2>
            <p className="section-subtitle">Transparent methodology so you can verify every number</p>
          </div>
          <div className="vp-methodology-grid">
            <div className="vp-method-card">
              <h4>Time Savings</h4>
              <ul>
                <li>Manual listing creation: timed at 7-10 min average across multiple dealership staff</li>
                <li>LeadeyePro posting: measured at 20-45 seconds including review</li>
                <li>Manual inquiry response: 3-5 min per message (find unit, check availability, type response)</li>
                <li>Price check: timed at 30-60 seconds per listing comparison, x150+ listings daily</li>
              </ul>
            </div>
            <div className="vp-method-card">
              <h4>Revenue Uplift</h4>
              <ul>
                <li>Lead response speed data: Harvard Business Review (2011), InsideSales.com, Drift (2023)</li>
                <li>After-hours inquiry volume: based on Facebook Marketplace usage patterns (peak: 6-10 PM)</li>
                <li>Conversion rate improvement: conservative 2-4x multiplier on qualified lead rate</li>
                <li>Deal profit margin: 6-10% gross on average vehicle price (8% used as midpoint)</li>
              </ul>
            </div>
            <div className="vp-method-card">
              <h4>Labor Cost Basis</h4>
              <ul>
                <li>BDC Representative avg salary: $38,000-48,000/yr (Glassdoor, Indeed, 2025)</li>
                <li>Fully loaded hourly cost: ~$22/hr including benefits and overhead</li>
                <li>Comparison: LeadeyePro replaces 80+ hrs/month of BDC-equivalent work</li>
                <li>Net: equivalent to 0.5-1.0 FTE at a fraction of the cost</li>
              </ul>
            </div>
            <div className="vp-method-card">
              <h4>Conservative Assumptions</h4>
              <ul>
                <li>All estimates use the lower end of measured ranges</li>
                <li>No value assigned to improved customer experience or brand consistency</li>
                <li>No value assigned to manager time freed from oversight</li>
                <li>Actual results vary by inventory size, market, and vehicle type</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="vp-cta">
        <div className="section-container">
          <div className="vp-cta-content">
            <h2>Ready to See These Numbers for Your Dealership?</h2>
            <p>
              We'll build a custom analysis for your specific inventory, volume, and market. 
              No commitment required — just real data on what LeadeyePro can do for your business.
            </p>
            <div className="vp-cta-buttons">
              <Link to="/#contact" className="btn btn-primary" data-testid="request-demo-cta">
                Request a Custom Analysis
              </Link>
              <a
                href="https://chromewebstore.google.com/detail/leadeyepro/lalbegpebklchglhpobgjnbjpieifcfe"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
              >
                Try It Free
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-brand">
            <span className="logo-text">LeadeyePro</span>
            <p>AI-powered sales automation for Facebook Marketplace</p>
          </div>
          <div className="footer-links">
            <Link to="/">Home</Link>
            <Link to="/value">Business Value</Link>
            <a href="https://chromewebstore.google.com/detail/leadeyepro/lalbegpebklchglhpobgjnbjpieifcfe" target="_blank" rel="noopener noreferrer">Add to Chrome</a>
          </div>
          <div className="footer-copyright">
            &copy; {new Date().getFullYear()} LeadeyePro. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
